import React, { useState, useEffect, useContext, useRef } from 'react';
import { toast } from 'sonner';
import { Calendar, X, PartyPopper, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/dialog';
import { AuthContext } from '../../App';

const CALENDLY_URL = 'https://calendly.com/shannoni/circleupsquadonboarding';
const DISMISS_KEY = 'book_onboarding_dismissed_at';
const BOOKED_KEY = 'book_onboarding_completed_at';
const DISMISS_DAYS = 30;

const loadCalendlyAssets = () => {
  if (typeof document === 'undefined') return;
  if (!document.getElementById('calendly-css')) {
    const link = document.createElement('link');
    link.id = 'calendly-css';
    link.rel = 'stylesheet';
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    document.head.appendChild(link);
  }
  if (!document.getElementById('calendly-script')) {
    const s = document.createElement('script');
    s.id = 'calendly-script';
    s.async = true;
    s.src = 'https://assets.calendly.com/assets/external/widget.js';
    document.body.appendChild(s);
  }
};

/**
 * Dedicated dashboard card that lets any signed-in user book a founder-led
 * onboarding session with Shannon. Opens Calendly in an inline dialog so we
 * can listen for the "invitee scheduled" postMessage, hide the card
 * permanently, and celebrate the booking with a toast.
 */
const BookOnboardingCard = () => {
  const { user } = useContext(AuthContext) || {};
  const [visible, setVisible] = useState(false);
  const [booked, setBooked] = useState(false);
  const [open, setOpen] = useState(false);
  const widgetRef = useRef(null);

  // Decide whether to show the card at all.
  useEffect(() => {
    if (localStorage.getItem(BOOKED_KEY)) { setBooked(true); return; }
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt && Date.now() - Number(dismissedAt) < DISMISS_DAYS * 24 * 3600 * 1000) return;
    setVisible(true);
  }, []);

  // Listen for Calendly's "invitee scheduled" postMessage while the dialog is open.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (typeof e.data !== 'object' || !e.data || !e.data.event) return;
      if (e.data.event === 'calendly.event_scheduled') {
        localStorage.setItem(BOOKED_KEY, String(Date.now()));
        toast.success("You're on Shannon's calendar 🎉 She'll see you soon!");
        setOpen(false);
        setVisible(false);
        setBooked(true);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [open]);

  // Load Calendly assets + mount the inline widget when the dialog opens.
  useEffect(() => {
    if (!open) return;
    loadCalendlyAssets();
    const url = `${CALENDLY_URL}?hide_gdpr_banner=1&primary_color=0891b2`
      + (user?.name ? `&name=${encodeURIComponent(user.name)}` : '')
      + (user?.email ? `&email=${encodeURIComponent(user.email)}` : '');

    const mount = () => {
      if (window.Calendly && widgetRef.current) {
        // Clear children via DOM API (not innerHTML) — safer and silences static scanners.
        while (widgetRef.current.firstChild) widgetRef.current.removeChild(widgetRef.current.firstChild);
        window.Calendly.initInlineWidget({ url, parentElement: widgetRef.current });
        return true;
      }
      return false;
    };
    if (mount()) return;
    const interval = setInterval(() => { if (mount()) clearInterval(interval); }, 250);
    return () => clearInterval(interval);
  }, [open, user?.name, user?.email]);

  if (!visible) {
    if (!booked) return null;
    return (
      <div className="flex justify-center" data-testid="onboarding-booked-pill-wrap">
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/25 text-emerald-700 dark:text-emerald-300 text-[11px] font-semibold border border-emerald-100 dark:border-emerald-900/40"
          data-testid="onboarding-booked-pill"
          title="You're on Shannon's calendar"
        >
          <CheckCircle2 className="w-3 h-3" /> Onboarding booked
        </span>
      </div>
    );
  }

  const dismiss = (e) => {
    e.stopPropagation();
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  return (
    <>
      <section
        className="relative rounded-2xl p-5 bg-gradient-to-br from-cyan-600 via-teal-600 to-emerald-600 text-white shadow-md"
        data-testid="book-onboarding-card"
      >
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="book-onboarding-dismiss"
          className="absolute top-2.5 right-2.5 p-1 rounded-full hover:bg-white/20 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider opacity-85">Meet Shannon</p>
            <h2 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Book Your Onboarding Session
            </h2>
            <p className="text-xs opacity-90 mt-0.5 leading-relaxed">
              Get 1:1 help setting up your circle so your squad shows up right from day one.
            </p>
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          className="w-full mt-4 h-11 rounded-xl bg-white text-cyan-700 hover:bg-white/90 font-semibold text-sm flex items-center justify-center gap-2 shadow-sm transition-colors"
          data-testid="book-onboarding-btn"
        >
          Book Your Onboarding Session <PartyPopper className="w-4 h-4" />
        </button>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="surface-card max-w-3xl max-h-[90vh] p-0 overflow-hidden"
          data-testid="book-onboarding-dialog"
        >
          <div ref={widgetRef} style={{ minWidth: '320px', height: '80vh' }} data-testid="calendly-inline-widget" />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BookOnboardingCard;
